import {AccentColorOptions} from 'epic-ue-theme';
import {ArrowLeft, ArrowRight} from '../../icons';
import React, {useCallback} from 'react';
import styled from 'styled-components';
import {Typography} from '../../typography';
import {useNextCreativeFeedDetail} from '../creatorfeed.hooks';
import {CreatorsFeedItemProps} from '../CreatorFeed.organism';
import {stopFocusOnClick} from 'epic-ue-shared';

const Styled = styled.div`
    position: absolute;
    bottom: 72px;
    ${(p) => p.theme.breakpoints.down('sm')} {
        position: static;
        bottom: 0;
        margin: 56px auto 0;
        width: calc(100% - 24px); /* outer padding of parent */
    }

    h4 {
        display: inline-block;
        padding: 0 24px;
        height: 24px;
    }
`;

const Flex = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Arrow = styled.button<{accentColor?: AccentColorOptions}>`
    background-color: transparent;
    border-color: transparent;
    color: ${(p) => p.theme.palette.common.white};
    svg {
        /* inline style is used on the icon color so !important is used to override the svg color */
        color: ${(p) => p.theme.palette.common.white} !important;
    }
    &.disabled svg {
        /* inline style is used on the icon color so !important is used to override the svg color */
        color: ${(p) => p.theme.palette.neutral.silver} !important;
        opacity: 0.3;
    }
    cursor: pointer;
    border: none;
    display: inline-block;
    padding: 0;
    align-items: center;
    .ue-icon-arrow-left,
    .ue-icon-arrow-right {
        transition: color 0.2s ${(p) => p.theme.easing};
    }
    &:hover {
        &.disabled {
            color: ${(p) => p.theme.palette.neutral.silver};
            cursor: default;
        }
        /* inline style is used on the icon color so !important is used to override on hover */
        &:not(.disabled) .arrow {
            color: ${(p) =>
                p.accentColor
                    ? p.theme.palette.accent.bright[p.accentColor]
                    : p.theme.palette.common.white} !important;
        }
    }
`;

export interface CreatorFeedDetailBrowserProps {
    cards: CreatorsFeedItemProps[];
    browseLabel?: string;
    accentColor?: AccentColorOptions;
}

export const CreatorFeedDetailBrowser = ({
    browseLabel = 'Browse Projects',
    cards = [],
    accentColor = 'sky'
}: CreatorFeedDetailBrowserProps): JSX.Element => {
    const {next, prev, prevDisabled, nextDisabled} = useNextCreativeFeedDetail(cards);

    const onClickPrev = useCallback(() => {
        if (!prevDisabled) {
            prev();
        }
    }, [prev, prevDisabled]);

    const onClickNext = useCallback(() => {
        if (!nextDisabled) {
            next();
        }
    }, [next, nextDisabled]);

    return (
        <Styled className={'creator-feed-detail-browser'}>
            <Flex>
                <Arrow
                    accentColor={accentColor}
                    className={`ue-carousel-btn ${prevDisabled ? 'disabled' : ''}`}
                    onClick={onClickPrev}
                    onMouseDown={stopFocusOnClick}>
                    <ArrowLeft color={prevDisabled ? 'disabled' : 'primary'} />
                </Arrow>
                <Typography variant="h4">{browseLabel}</Typography>
                <Arrow
                    accentColor={accentColor}
                    className={`ue-carousel-btn ${nextDisabled ? 'disabled' : ''}`}
                    onClick={onClickNext}
                    onMouseDown={stopFocusOnClick}>
                    <ArrowRight color={nextDisabled ? 'disabled' : 'primary'} />
                </Arrow>
            </Flex>
        </Styled>
    );
};
